const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface DayWindow {
    /** YYYY-MM-DD, UTC, inclusive. */
    from: string;
    to: string;
}

function toTime(day: string): number | null {
    if (!DAY_RE.test(day)) return null;
    const time = Date.parse(`${day}T00:00:00Z`);
    // Date.parse rolls 2026-02-30 over to March; a real day reads back the same.
    if (Number.isNaN(time) || new Date(time).toISOString().slice(0, 10) !== day) return null;
    return time;
}

function toDay(time: number): string {
    return new Date(time).toISOString().slice(0, 10);
}

export function isDay(day: string): boolean {
    return toTime(day) !== null;
}

export function addDays(day: string, days: number): string {
    const time = toTime(day);
    if (time === null) throw new Error(`Not a day: ${day}`);
    return toDay(time + days * DAY_MS);
}

/** Days from `from` to `to`, both counted; 0 when either is not a day or `to` is earlier. */
export function daysInclusive(from: string, to: string): number {
    const start = toTime(from);
    const end = toTime(to);
    if (start === null || end === null || end < start) return 0;
    return Math.round((end - start) / DAY_MS) + 1;
}

export function todayUtc(now: Date = new Date()): string {
    return toDay(now.getTime());
}

/**
 * The window as consecutive chunks of `days` days each, the last one possibly shorter, so
 * a long history runs as requests the server accepts one at a time.
 */
export function chunkWindow(from: string, to: string, days: number): DayWindow[] {
    const start = toTime(from);
    const end = toTime(to);
    if (start === null || end === null || end < start || days < 1) return [];
    const chunks: DayWindow[] = [];
    for (let first = start; first <= end; first += days * DAY_MS) {
        const last = Math.min(first + (days - 1) * DAY_MS, end);
        chunks.push({ from: toDay(first), to: toDay(last) });
    }
    return chunks;
}
